import { Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import { Image as ImageIcon } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";

export const CompanyLogoPanel: React.FC<{
    logoFile: File | null;
    logoPreview: string;
    onLogoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ logoPreview, onLogoUpload }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    // Reset error state when logoPreview changes
    useEffect(() => {
        setImageError(false);
        setImageLoaded(false);
    }, [logoPreview]);

    const handleImageLoad = () => {
        setImageLoaded(true);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageError(true);
        setImageLoaded(false);
        console.error("Failed to load logo image:", logoPreview);
    };

    const showLogo = logoPreview && logoPreview !== "/src/assets/CambreLogo.png";

    return (
        <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
                <Typography
                    variant="h6"
                    sx={{
                        fontSize: "1.25rem",
                        fontWeight: 500,
                        color: "text.primary",
                        mb: 2,
                    }}
                >
                    Company Logo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload your company logo that will appear in the document header.
                </Typography>

                {showLogo && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            p: 2,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            backgroundColor: "background.paper",
                        }}
                    >
                        {imageError ? (
                            <Box
                                sx={{
                                    width: 200,
                                    height: 60,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1px dashed red",
                                    borderRadius: 1,
                                    backgroundColor: "error.light",
                                    color: "error.dark",
                                }}
                            >
                                <Typography variant="caption" color="error">
                                    Failed to load image
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {!imageLoaded && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="caption">Loading image...</Typography>
                                    </Box>
                                )}
                                <img
                                    src={logoPreview}
                                    alt="Company Logo"
                                    style={{
                                        maxHeight: 60,
                                        maxWidth: "100%",
                                        objectFit: "contain",
                                        display: imageLoaded ? "block" : "none",
                                    }}
                                    onLoad={handleImageLoad}
                                    onError={handleImageError}
                                />
                            </>
                        )}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                                Current Logo Above
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Recommended: PNG or JPG, max 900px width
                            </Typography>
                        </Box>
                    </Box>
                )}

                <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="logo-upload"
                    type="file"
                    onChange={onLogoUpload}
                />
                <label htmlFor="logo-upload">
                    <Box
                        component="div"
                        sx={{
                            border: "2px dashed",
                            borderColor: "divider",
                            borderRadius: 1,
                            p: 4,
                            textAlign: "center",
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                                borderColor: "primary.main",
                                backgroundColor: "action.hover",
                            },
                        }}
                    >
                        <ImageIcon size={32} color="#666" />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Click to upload new logo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            PNG, JPG up to 2MB
                        </Typography>
                    </Box>
                </label>
            </CardContent>
        </Card>
    );
};
